import { createContext, useContext, useEffect, useState } from "react";

import { Box, Typography, LinearProgress, Button } from "@mui/material";

import Banner from "../Component/common/Banner";

import { Outlet, useParams } from "react-router-dom";
import Footer from "../Component/common/Footer";
import logo from "../assets/logo/logo.svg";
import shlogo from "../assets/img/shiro.jpg";
import { Helmet, HelmetProvider } from "react-helmet-async";

import Lottie from "react-lottie";
import anime from "../assets/anime/uKGMJQo3HR.json";
import { IoIosArrowBack } from "react-icons/io";
import { useGetQuery } from "../api/useQuery";

// Create a context for step management
const StepContext = createContext();

// Hook to use step context
export const useStep = () => {
  const context = useContext(StepContext);
  if (!context) {
    throw new Error("useStep must be used within a StepProvider");
  }
  return context;
};

const Home = () => {
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: anime,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const { name, id } = useParams();

  // Add step state management
  const [step, setStep] = useState(0);

  // Function to reset step to 0
  const resetStep = () => {
    setStep(0);
  };

  const { data, isPending } = useGetQuery(
    ["event", id, name],
    `restaurantevent/${id}/name/${name}`,
  );

  const event_data = data?.result;

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{`${name} | Reisty`}</title>
          <meta name="description" content={data?.EventDescription} />
          <meta property="og:type" content={"website"} />
          <meta property="og:title" content={`${name} | Reisty`} />
          <meta property="og:url" content="https://www.ticket.reisty.com/" />
          <meta property="og:description" content={data?.EventDescription} />
          <meta property="og:image" content={data?.EventImageUrl} />
          <meta name="twitter:creator" content={`${name} | Reisty`} />
          <meta name="twitter:card" content={"website"} />
          <meta name="twitter:title" content={`${name} | Reisty`} />
          <meta name="twitter:description" content={data?.EventDescription} />
          <meta name="twitter:image" content={data?.EventImageUrl} />
        </Helmet>
        {isPending ? (
          <>
            <Box
              sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fff",
              }}
            >
              <LinearProgress sx={{ width: "40%" }} />
              <Typography sx={{ fontSize: "12px", mt: 1, textAlign: "center" }}>
                Getting <b>{name}</b> event details
              </Typography>
            </Box>
          </>
        ) : (
          <>
            {!event_data || event_data?.length === 0 ? (
              <>
                <Box
                  sx={{
                    bgcolor: "#eee",
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <Lottie options={defaultOptions} width={200} height={200} />
                  <Typography sx={{ fontWeight: 500, fontSize: "30px" }}>
                    Event Not Found
                  </Typography>

                  <a href="https://www.reisty.com/">
                    <Typography
                      color="primary"
                      sx={{
                        textDecoration: "underline",
                        fontSize: "12px",
                        mt: 1,
                      }}
                    >
                      Explore available events
                    </Typography>
                  </a>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ backgroundColor: "rgba(188, 23, 47, 0.05)" }}>
                  <Box
                    sx={{
                      width: {
                        xl: "30%",
                        lg: "40%",
                        md: "50%",
                        sm: "65%",
                        xs: "100%",
                      },
                      margin: "0 auto",
                      backgroundColor: "#fff",
                    }}
                  >
                    {step !== 0 && (
                      <Button
                        onClick={resetStep}
                        sx={{
                          pt: 2,
                          display: "flex",
                          alignItems: "center",
                          columnGap: 3,
                        }}
                      >
                        <IoIosArrowBack style={{}} />
                        <Typography sx={{ fontWeight: 500, fontSize: "13px" }}>
                          Back to Ticket
                        </Typography>
                      </Button>
                    )}
                    <Box
                      sx={{
                        display: "grid",
                        placeContent: "center",
                        height: "60px",
                        borderBottom: "5px solid #bc172f",
                      }}
                    >
                      {name === EVENT_SHIRO ? (
                        <img src={shlogo} width={40} alt="reisty_logo" />
                      ) : (
                        <img src={logo} alt="reisty_logo" />
                      )}
                    </Box>

                    <Banner data={event_data} />
                    <StepContext.Provider value={{ step, setStep, resetStep }}>
                      <Outlet />
                    </StepContext.Provider>
                  </Box>
                  <Footer />
                </Box>
              </>
            )}
          </>
        )}
      </HelmetProvider>
    </>
  );
};

export default Home;

export const EVENT_SHIRO = "Tappayanki";
